import React, { useEffect, useState } from "react";
import { ShoppingBag, Sparkles } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * KidsShop — tiny kid-branded storefront.
 *
 * Products come from /api/kids-public/shop which seeds a starter catalog
 * and blends in any `products` tagged `kids`. No real checkout here yet
 * — just a friendly "Ask your grown-up to get it" confirmation so kids
 * can't spend money without a parent.
 */
export default function KidsShop() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    const q = cat === "all" ? "" : `?category=${cat}`;
    api.get(`/kids-public/shop${q}`).then((r) => setItems(r.data?.items || [])).catch(() => {});
  }, [cat]);

  const tap = (item) => {
    toast.success(`Great choice! Ask your grown-up to buy "${item.title}" for you 💛`);
  };

  const CATS = [
    { key: "all",    label: "All"    },
    { key: "toys",   label: "Toys"   },
    { key: "books",  label: "Books"  },
    { key: "crafts", label: "Crafts" },
  ];

  return (
    <div data-testid="kids-shop-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto text-white">
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-8 mt-3 bg-gradient-to-br from-amber-400 via-pink-500 to-fuchsia-600 shadow-2xl ring-4 ring-white/25">
        <div className="absolute -top-8 -right-8 opacity-25 pointer-events-none text-[10rem]">🛍️</div>
        <div className="relative flex items-center gap-3 flex-wrap">
          <img src="/kids-learn-play-logo.png" alt="" className="w-20 sm:w-28 h-auto drop-shadow-2xl" />
          <div>
            <div className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 rounded-full backdrop-blur"><Sparkles className="w-3 h-3" /> Kid Shop</div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter drop-shadow mt-1">Goodies</h1>
            <p className="text-white/90 text-sm">Tap something you love — we'll tell your grown-up!</p>
          </div>
        </div>
      </div>

      <div data-testid="shop-cats" className="mt-5 flex gap-2 overflow-x-auto pb-1">
        {CATS.map((c) => (
          <button
            key={c.key}
            data-testid={`shop-cat-${c.key}`}
            onClick={() => setCat(c.key)}
            className={`shrink-0 font-black text-sm px-4 py-2 rounded-full transition ${cat === c.key ? "bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white shadow-lg" : "bg-white/10 text-white/80 hover:text-white"}`}
          >{c.label}</button>
        ))}
      </div>

      <div data-testid="shop-grid" className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <button
            key={it.id}
            data-testid={`shop-item-${it.id}`}
            onClick={() => tap(it)}
            className={`rounded-2xl overflow-hidden bg-gradient-to-br ${it.color || "from-pink-400 to-fuchsia-500"} ring-2 ring-white/25 shadow-lg hover:scale-[1.04] transition text-left p-3 flex flex-col`}
          >
            <div className="aspect-square rounded-xl bg-white/15 flex items-center justify-center">
              {it.image_url
                ? <img src={it.image_url} alt="" className="w-full h-full object-cover rounded-xl" />
                : <span className="text-[5rem] drop-shadow-lg">{it.emoji}</span>}
            </div>
            <div className="mt-2 text-white font-black text-sm leading-tight">{it.title}</div>
            <div className="mt-1 inline-block bg-white/85 text-[#222] font-black text-xs px-2 py-0.5 rounded-full self-start">${it.price?.toFixed(2) || "—"}</div>
          </button>
        ))}
        {items.length === 0 && <div className="col-span-full text-white/50 text-center py-10" data-testid="shop-empty">No items in this category</div>}
      </div>

      <div className="mt-6 inline-flex items-center gap-2 text-xs text-white/60">
        <ShoppingBag className="w-4 h-4" /> Always ask a grown-up before buying anything online!
      </div>
    </div>
  );
}
